import {
  FormControl,
  FormLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DosageInstructions, MedicalSupply } from "src/api/types";
import { MedicationType } from "src/components/PrescriptionRequest/PrescriptionRequest";
import { Vial } from "../../PrescribeModal";
import { ChangeEvent } from "react";
import { useGetInstructionsByMedicationIdQuery } from "src/api/services/medicationsApi";
import { skipToken } from "@reduxjs/toolkit/query";

interface InputProps {
  idx: number;
  vials: Vial[];
  medication: MedicationType;
  medicationOptions: MedicationType[];
  supplies: MedicalSupply[];
  supplyId: number | null;
  daysSupply: number | undefined;
  handleVial: (i: number, c: Partial<Vial>) => void;
  handleInstructions: (
    i: number,
    o: DosageInstructions[],
  ) => (e: ChangeEvent<HTMLInputElement>) => void;
  handleSupplies: (event: ChangeEvent<HTMLInputElement>) => void;
  matchedMedicationOption: MedicationType | undefined;
  totalDispenseQuantity: number;
}

const PrescriptionInputs = ({
  vials,
  idx,
  medication,
  medicationOptions,
  supplies,
  supplyId,
  daysSupply,
  handleVial,
  handleInstructions,
  handleSupplies,
  matchedMedicationOption,
}: InputProps) => {
  const { data: instructions = [] } = useGetInstructionsByMedicationIdQuery(
    matchedMedicationOption?.id ? matchedMedicationOption.id : skipToken,
  );

  return (
    <Stack gap={1} width="100%" key={idx}>
      <Typography fontWeight={600} sx={{ textDecoration: "underline" }}>
        Medication #{idx + 1}
      </Typography>
      <FormControl sx={{ gap: 1 }} required>
        <FormLabel>Medication:</FormLabel>
        <TextField
          required
          select
          disabled={idx === 0}
          value={vials[idx]?.medicationId ?? ""}
          onChange={(e) =>
            handleVial(idx, {
              medicationId: Number(e.target.value),
            })
          }
        >
          {medicationOptions?.map((medication, idx) => (
            <MenuItem key={idx} value={medication.id}>
              {medication.medicationName} - {medication.dosage}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
      <Stack justifyContent="center" gap="16px">
        <FormControl sx={{ gap: 1 }}>
          <FormLabel>Dosage instruction:</FormLabel>
          {medication.medicationName === "Metformin" ? (
            [...Array((daysSupply || 0) / 30)].map((_, idx) => (
              <TextField
                required
                select
                key={idx}
                label={`Month ${idx + 1}`}
                value={vials[idx]?.instruction?.value ?? ""}
                onChange={handleInstructions(idx, instructions)}
              >
                {instructions.map((option, idx) => {
                  return (
                    <MenuItem key={idx} value={option.instruction.value}>
                      {option.instruction.value}
                    </MenuItem>
                  );
                })}
              </TextField>
            ))
          ) : (
            <TextField
              required
              select
              value={vials[idx]?.instruction?.value ?? ""}
              onChange={handleInstructions(idx, instructions)}
            >
              {instructions.map((option, idx) => {
                return (
                  <MenuItem key={idx} value={option.instruction.value}>
                    {option.instruction.value}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
        </FormControl>

        {medication.is_glp1 === true && supplies.length > 0 ? (
          <FormControl sx={{ gap: 1 }}>
            <FormLabel>Medical supplies:</FormLabel>
            <TextField
              required
              select
              value={supplyId}
              onChange={handleSupplies}
              disabled={supplies.length === 1}
            >
              {supplies.map((supply) => (
                <MenuItem key={supply.id} value={supply.id}>
                  {supply.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default PrescriptionInputs;
